import { IWebsiteBuilderIconSettings, websiteBuilderNormalizeIconSettings } from "../element/websiteBuilderIcon";
import classNames from "classnames";
import { IWebsiteBuilderLinkSettings, websiteBuilderGenerateDefaultLinkSettings, websiteBuilderNormalizeLinkSettings } from "../element/websiteBuilderLink";
import { clamp } from "lodash";
import { CSSProperties } from "react";
import { IWebsiteBuilderContainerBase, IWebsiteBuilderContainerDataBase } from "./websiteBuilderContainer";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import {
  IWebsiteBuilderSectionEntityStretch,
} from "../websiteBuilderSectionEntity";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import {
  websiteBuilderNormalizeHexColor,
  websiteBuilderNormalizeValue,
} from "../../schema/normalization/websiteBuilderNormalizeValue";
import { TWebsiteBuilderDevice } from "../../website/shared/device";
import {
  WB_MARQUEE_FONT_SIZE_DESKTOP_CSS_VARIABLE,
  WB_MARQUEE_FONT_SIZE_TABLET_CSS_VARIABLE,
  WB_MARQUEE_FONT_SIZE_MOBILE_CSS_VARIABLE,
  WB_MARQUEE_ANIMATION_DURATION_CSS_VARIABLE,
} from "../../website/shared/cssVariables";

export const WEBSITE_BUILDER_MARQUEE_MIN_FONT_SIZE = 12;
export const WEBSITE_BUILDER_MARQUEE_MAX_FONT_SIZE = 48;

export const WEBSITE_BUILDER_MARQUEE_MIN_DURATION = 5000;
export const WEBSITE_BUILDER_MARQUEE_MAX_DURATION = 20000;

export type TWebsiteBuilderMarqueeFontSize = {
  [K in TWebsiteBuilderDevice]: number;
};

interface IWebsiteBuilderMarqueeItemBase {
  id: string;
}

type TWebsiteBuilderMarqueeIconItemSettings = IWebsiteBuilderIconSettings;

export interface IWebsiteBuilderMarqueeIconItem extends IWebsiteBuilderMarqueeItemBase {
  type: "icon";
  settings: TWebsiteBuilderMarqueeIconItemSettings;
}

type TWebsiteBuilderMarqueeLinkItemSettings = IWebsiteBuilderLinkSettings;

export interface IWebsiteBuilderMarqueeLinkItem extends IWebsiteBuilderMarqueeItemBase {
  type: "link";
  settings: TWebsiteBuilderMarqueeLinkItemSettings;
}

export interface IWebsiteBuilderMarqueeParagraphItemSettings {
  text: string;
  color: string;
}

export interface IWebsiteBuilderMarqueeParagraphItem extends IWebsiteBuilderMarqueeItemBase {
  type: "paragraph";
  settings: IWebsiteBuilderMarqueeParagraphItemSettings;
}

export type TWebsiteBuilderMarqueeItem = IWebsiteBuilderMarqueeIconItem | IWebsiteBuilderMarqueeLinkItem | IWebsiteBuilderMarqueeParagraphItem;
export type TWebsiteBuilderMarqueeItemType = TWebsiteBuilderMarqueeItem["type"];

export type TWebsiteBuilderMarqueeDirection = "right-to-left" | "left-to-right";

function isWebsiteBuilderMarqueeDirection(value: string): value is TWebsiteBuilderMarqueeDirection {
  return value === "right-to-left" || value === "left-to-right";
}

export interface IWebsiteBuilderMarqueeSettings {
  items: TWebsiteBuilderMarqueeItem[];
  fontSize: TWebsiteBuilderMarqueeFontSize;
  showSeparator: boolean;
  separatorColor: string;
  backgroundColor: string;
  direction: TWebsiteBuilderMarqueeDirection;
  duration: number;
}

interface IWebsiteBuilderMarqueeData extends IWebsiteBuilderContainerDataBase {
  type: "marquee";
  settings: IWebsiteBuilderMarqueeSettings;
  stretch: IWebsiteBuilderSectionEntityStretch;
}

export interface IWebsiteBuilderMarquee extends IWebsiteBuilderContainerBase<[]> {
  container: IWebsiteBuilderMarqueeData;
}

export function isWebsiteBuilderMarquee(node: any): node is IWebsiteBuilderMarquee {
  return node?.type === "container" && node.container?.type === "marquee";
}

function generateDefaultMarqueeSettings(): IWebsiteBuilderMarqueeSettings {
  return {
    items: [
      {
        id: crypto.randomUUID(),
        type: "paragraph",
        settings: {
          text: "Paragraph", // TODO: Translation
          color: null,
        },
      },
      {
        id: crypto.randomUUID(),
        type: "link",
        settings: websiteBuilderGenerateDefaultLinkSettings(),
      },
    ],
    fontSize: {
      desktop: null,
      tablet: null,
      mobile: null,
    },
    showSeparator: true,
    separatorColor: null,
    backgroundColor: null,
    direction: "right-to-left",
    duration: 10000,
  };
}

export function websiteBuilderGenerateDefaultMarquee(): IWebsiteBuilderMarquee {
  return {
    id: crypto.randomUUID(),
    type: "container",
    container: {
      type: "marquee",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 400, height: 75 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 75 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 200, height: 75 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultMarqueeSettings(),
      stretch: {
        enabled: true,
        margin: 0,
      },
    },
    children: [],
  };
}

export function websiteBuilderNormalizeMarqueeSettings(
  partialSettings: Partial<IWebsiteBuilderMarqueeSettings>,
): IWebsiteBuilderMarqueeSettings {
  const defaultSettings = generateDefaultMarqueeSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { items, fontSize, showSeparator, separatorColor, backgroundColor, direction, duration } = partialSettings;

  let normalizedItems: TWebsiteBuilderMarqueeItem[] = [];

  if (Array.isArray(items)) {
    items.forEach(item => {
      if (item?.type === "icon") {
        normalizedItems.push({
          id: typeof item.id === "string" ? item.id : crypto.randomUUID(),
          type: "icon",
          settings: websiteBuilderNormalizeIconSettings(item.settings),
        });
      } else if (item?.type === "link") {
        normalizedItems.push({
          id: typeof item.id === "string" ? item.id : crypto.randomUUID(),
          type: "link",
          settings: websiteBuilderNormalizeLinkSettings(item.settings),
        });
      } else if (item?.type === "paragraph") {
        normalizedItems.push({
          id: typeof item.id === "string" ? item.id : crypto.randomUUID(),
          type: "paragraph",
          settings: {
            text: typeof item.settings?.text === "string" ? item.settings.text : "",
            color: websiteBuilderNormalizeHexColor(item.settings?.color, null, false),
          },
        });
      }
    });
  } else {
    normalizedItems = defaultSettings.items;
  }

  const normalizedFontSize: TWebsiteBuilderMarqueeFontSize = fontSize == null ? defaultSettings.fontSize : {
    desktop: websiteBuilderNormalizeValue({
      type: "number",
      value: fontSize.desktop,
      defaultValue: defaultSettings.fontSize.desktop,
      required: false,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_MARQUEE_MIN_FONT_SIZE, WEBSITE_BUILDER_MARQUEE_MAX_FONT_SIZE),
    }),
    tablet: websiteBuilderNormalizeValue({
      type: "number",
      value: fontSize.tablet,
      defaultValue: defaultSettings.fontSize.tablet,
      required: false,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_MARQUEE_MIN_FONT_SIZE, WEBSITE_BUILDER_MARQUEE_MAX_FONT_SIZE),
    }),
    mobile: websiteBuilderNormalizeValue({
      type: "number",
      value: fontSize.mobile,
      defaultValue: defaultSettings.fontSize.mobile,
      required: false,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_MARQUEE_MIN_FONT_SIZE, WEBSITE_BUILDER_MARQUEE_MAX_FONT_SIZE),
    }),
  };

  const normalizedMarqueeSettings: IWebsiteBuilderMarqueeSettings = {
    items: normalizedItems,
    fontSize: normalizedFontSize,
    showSeparator: websiteBuilderNormalizeValue({
      type: "boolean",
      value: showSeparator,
      defaultValue: defaultSettings.showSeparator,
      required: true,
    }),
    separatorColor: websiteBuilderNormalizeHexColor(separatorColor, defaultSettings.separatorColor, false),
    backgroundColor: websiteBuilderNormalizeHexColor(backgroundColor, defaultSettings.backgroundColor, false),
    direction: websiteBuilderNormalizeValue({
      type: "string",
      value: direction,
      defaultValue: defaultSettings.direction,
      required: true,
      validate: isWebsiteBuilderMarqueeDirection,
    }) as TWebsiteBuilderMarqueeDirection,
    duration: websiteBuilderNormalizeValue({
      type: "number",
      value: duration,
      defaultValue: defaultSettings.duration,
      required: true,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_MARQUEE_MIN_DURATION, WEBSITE_BUILDER_MARQUEE_MAX_DURATION),
    }),
  };

  return normalizedMarqueeSettings;
}

interface IWebsiteBuilderMarqueeDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  trackClassName: string;
  separatorClassName: string;
  separatorStyleProperties: CSSProperties;
  separatorStyleDeclaration: string;
}

export function websiteBuilderGenerateMarqueeDesignStyles(
  settings: IWebsiteBuilderMarqueeSettings,
): IWebsiteBuilderMarqueeDesignStyles {
  const styleProperties: CSSProperties = {
    backgroundColor: settings.backgroundColor,
    ...(settings.fontSize.desktop != null ? { [WB_MARQUEE_FONT_SIZE_DESKTOP_CSS_VARIABLE]: `${settings.fontSize.desktop}px` } : {}),
    ...(settings.fontSize.tablet != null ? { [WB_MARQUEE_FONT_SIZE_TABLET_CSS_VARIABLE]: `${settings.fontSize.tablet}px` } : {}),
    ...(settings.fontSize.mobile != null ? { [WB_MARQUEE_FONT_SIZE_MOBILE_CSS_VARIABLE]: `${settings.fontSize.mobile}px` } : {}),
    [WB_MARQUEE_ANIMATION_DURATION_CSS_VARIABLE]: `${settings.duration}ms`,
  };

  let styleDeclaration = "";

  if (styleProperties.backgroundColor != null) {
    styleDeclaration += `background-color: ${styleProperties.backgroundColor};`;
  }

  if (settings.fontSize.desktop != null) {
    styleDeclaration += `${WB_MARQUEE_FONT_SIZE_DESKTOP_CSS_VARIABLE}: ${settings.fontSize.desktop}px;`;
  }
  if (settings.fontSize.tablet != null) {
    styleDeclaration += `${WB_MARQUEE_FONT_SIZE_TABLET_CSS_VARIABLE}: ${settings.fontSize.tablet}px;`;
  }
  if (settings.fontSize.mobile != null) {
    styleDeclaration += `${WB_MARQUEE_FONT_SIZE_MOBILE_CSS_VARIABLE}: ${settings.fontSize.mobile}px;`;
  }

  styleDeclaration += `${WB_MARQUEE_ANIMATION_DURATION_CSS_VARIABLE}: ${settings.duration}ms;`;

  const separatorStyleProperties: CSSProperties = {
    color: settings.separatorColor,
  };

  let separatorStyleDeclaration = "";

  if (separatorStyleProperties.color != null) {
    separatorStyleDeclaration += `color: ${separatorStyleProperties.color};`;
  }

  return {
    className: "website-builder-marquee",
    styleProperties,
    styleDeclaration,
    trackClassName: classNames(
      "website-builder-marquee-track",
      `marquee-track-${settings.direction}`,
    ),
    separatorClassName: classNames("website-builder-marquee-separator", {
      "marquee-separator-hidden": !settings.showSeparator,
    }),
    separatorStyleProperties,
    separatorStyleDeclaration,
  };
}

export function websiteBuilderGenerateMarqueeIconItemDesignStyles(
  settings: TWebsiteBuilderMarqueeIconItemSettings,
): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    color: settings.color,
  };

  let styleDeclaration = "";

  if (styleProperties.color != null) {
    styleDeclaration += `color: ${styleProperties.color};`;
  }

  return {
    className: "website-builder-marquee-icon-item",
    styleProperties,
    styleDeclaration,
  };
}

export function websiteBuilderGenerateMarqueeLinkItemDesignStyles(
  settings: TWebsiteBuilderMarqueeLinkItemSettings,
): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    color: settings.color,
  };

  let styleDeclaration = "";

  if (styleProperties.color != null) {
    styleDeclaration += `color: ${styleProperties.color};`;
  }

  return {
    className: classNames("website-builder-marquee-link-item", {
      "underline-link": settings.underline,
    }),
    styleProperties,
    styleDeclaration,
  };
}

export function websiteBuilderGenerateMarqueeParagraphItemDesignStyles(
  settings: IWebsiteBuilderMarqueeParagraphItemSettings,
): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    color: settings.color,
  };

  let styleDeclaration = "";

  if (styleProperties.color != null) {
    styleDeclaration += `color: ${styleProperties.color};`;
  }

  return {
    className: "website-builder-marquee-paragraph-item",
    styleProperties,
    styleDeclaration,
  };
}
